import React, { useCallback, useEffect, useState } from 'react';
import './RetailIntelligenceAdminPage.css';

const ENV_API_BASE = process.env.REACT_APP_RETAIL_INTELLIGENCE_API_URL || '';
const LOCAL_API_BASE = 'http://localhost:8220';
const PROXY_API_BASE = '/retail-intelligence-api';
const DEFAULT_API_BASE = ENV_API_BASE || (isBrowserLocalhost() ? LOCAL_API_BASE : PROXY_API_BASE);
const API_KEY_HEADER = 'X-Retail-Intelligence-API-Key';
const STORAGE_KEY = 'retail-intelligence-admin-api-base';

const EMPTY_NUTRITION = {
  calories_kcal: null,
  sodium_mg: null,
  sugars_g: null,
  carbohydrate_g: null,
  protein_g: null,
  fat_g: null,
  saturated_fat_g: null,
  trans_fat_g: null,
  cholesterol_mg: null,
  fiber_g: null,
};

const TABS = [
  { key: 'products', label: '상품' },
  { key: 'standardProducts', label: '대표 영양' },
  { key: 'nutrition', label: '영양 매핑' },
  { key: 'inventory', label: 'Mock 재고' },
  { key: 'policies', label: '정책' },
];

const HEALTH_TAG_OPTIONS = [
  'LOW_SODIUM',
  'LOW_SUGAR',
  'GLUCOSE_BALANCE',
  'HIGH_FIBER',
  'PROTEIN_SUPPORT',
  'POTASSIUM_SUPPORT',
  'CALCIUM_SUPPORT',
  'IRON_SUPPORT',
  'LOW_SAT_FAT',
  'HEART_FRIENDLY',
  'DIGESTIVE_SUPPORT',
  'WEIGHT_CONTROL',
];

function readInitialApiBase() {
  try {
    const storedApiBase = window.localStorage.getItem(STORAGE_KEY);
    if (shouldUseStoredApiBase(storedApiBase)) {
      return storedApiBase;
    }
    if (storedApiBase) {
      window.localStorage.removeItem(STORAGE_KEY);
    }
    return DEFAULT_API_BASE;
  } catch {
    return DEFAULT_API_BASE;
  }
}

function normalizeBaseUrl(value) {
  return String(value || '').trim().replace(/\/$/, '');
}

function isBrowserLocalhost() {
  if (typeof window === 'undefined') {
    return false;
  }
  return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
}

function isLocalhostApiBase(value) {
  const normalizedValue = normalizeBaseUrl(value);
  if (!normalizedValue) {
    return false;
  }
  try {
    const parsedUrl = new URL(normalizedValue, window.location.origin);
    return ['localhost', '127.0.0.1', '::1'].includes(parsedUrl.hostname);
  } catch {
    return /^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])(?::|\/|$)/i.test(normalizedValue);
  }
}

function shouldUseStoredApiBase(value) {
  const normalizedValue = normalizeBaseUrl(value);
  if (!normalizedValue) {
    return false;
  }
  return isBrowserLocalhost() || !isLocalhostApiBase(normalizedValue);
}

function rememberApiBase(value) {
  try {
    const normalizedValue = normalizeBaseUrl(value);
    if (shouldUseStoredApiBase(normalizedValue)) {
      window.localStorage.setItem(STORAGE_KEY, normalizedValue);
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // localStorage is optional in embedded test environments.
  }
}

function withApiKeyHeader(options, apiKey) {
  const normalizedApiKey = String(apiKey || '').trim();
  if (!normalizedApiKey) {
    return options;
  }
  return {
    ...options,
    headers: {
      ...(options.headers || {}),
      [API_KEY_HEADER]: normalizedApiKey,
    },
  };
}

async function requestJson(apiBase, path, options = {}, apiKey = '') {
  const endpoint = `${normalizeBaseUrl(apiBase)}${path}`;
  let response;
  try {
    response = await fetch(endpoint, withApiKeyHeader(options, apiKey));
  } catch (err) {
    const error = new Error(`API 서버에 연결할 수 없습니다. ${normalizeBaseUrl(apiBase)} 실행 상태를 확인하세요.`);
    error.cause = err;
    throw error;
  }

  const raw = await response.text();
  let body = {};
  try {
    body = raw ? JSON.parse(raw) : {};
  } catch {
    body = { raw };
  }
  if (!response.ok) {
    const error = new Error(body?.detail?.message || body?.detail || raw || `HTTP ${response.status}`);
    error.status = response.status;
    error.body = body;
    throw error;
  }
  return body;
}

function readJson(value, fallback) {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    throw new Error('JSON 형식이 올바르지 않습니다.');
  }
}

function formatJson(value) {
  return JSON.stringify(value || {}, null, 2);
}

function formatPercent(value) {
  const numericValue = Number(value || 0);
  return `${Math.round(numericValue * 100)}%`;
}

function toCsv(value) {
  return Array.isArray(value) ? value.join(', ') : '';
}

function fromCsv(value) {
  return String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function productToForm(item = {}) {
  return {
    standard_product_id: item.standard_product_id || '',
    standard_product_name: item.standard_product_name || item.product_name || '',
    category_id: item.category_id || '',
    category_name: item.category_name || item.category || '',
    product_type_code: item.product_type_code || item.product_type || 'UNKNOWN',
    product_type_name: item.product_type_name || '',
    gs_product_id: item.gs_product_id || '',
    gs_product_name: item.gs_product_name || '',
    is_primary_product: Boolean(item.is_primary_product),
    sku_id: item.sku_id || '',
    sku_name: item.sku_name || '',
    sku_sales_channel_scope: toCsv(item.sku_sales_channel_scope || item.sales_channel_scope),
    sku_availability_scope: toCsv(item.sku_availability_scope || item.availability_scope),
    sku_pilot_enabled: item.sku_pilot_enabled !== false,
    barcode: item.barcode || '',
    gtin: item.gtin || '',
    item_report_no: item.item_report_no || '',
    manufacturer_name: item.manufacturer_name || '',
    product_health_tags: toCsv(item.product_health_tags),
    product_health_tag_scores: formatJson(item.product_health_tag_scores || {}),
    raw_payload: formatJson(item.raw_payload || {}),
  };
}

function standardFoodToForm(item = {}) {
  return {
    standard_product_id: item.standard_product_id || item.standard_item_id || '',
    edible_state: item.edible_state || 'raw',
    serving_basis: item.serving_basis || '100g',
    nutrition_source: item.nutrition_source || 'RDA',
    source_confidence: item.source_confidence ?? 1,
    source_ref: formatJson(item.source_ref || {}),
    nutrition_facts: formatJson(item.nutrition_facts || EMPTY_NUTRITION),
  };
}

function nutritionToForm(item = {}) {
  return {
    gs_product_id: item.gs_product_id || '',
    standard_product_id: item.standard_product_id || item.standard_item_id || '',
    mapping_level: item.mapping_level || 'SKU',
    serving_basis: item.serving_basis || '100g',
    nutrition_source: item.nutrition_source || 'GS',
    nutrition_match_status: item.nutrition_match_status || 'GS_PROVIDED',
    match_confidence: item.match_confidence ?? 1,
    source_ref: formatJson(item.source_ref || {}),
    nutrition_facts: formatJson(item.nutrition_facts || EMPTY_NUTRITION),
    verified: item.verified !== false,
  };
}

function policyToForm(item = {}) {
  return {
    policy_version: item.policy_version || '',
    active: Boolean(item.active),
    policy_payload: formatJson(item.policy_payload || {}),
  };
}

function inventoryToForm(item = {}) {
  return {
    store_type: item.store_type || 'GS_THE_FRESH',
    store_code: item.store_code || 'REPRESENTATIVE_STORE',
    sku_id: item.sku_id || '',
    gs_product_id: item.gs_product_id || '',
    available: item.available !== false,
    stock_quantity: item.stock_quantity ?? '',
    inventory_status: item.inventory_status || 'IN_STOCK',
    source: item.source || 'MOCK_ADMIN',
    raw_payload: formatJson(item.raw_payload || {}),
  };
}

function RetailIntelligenceAdminPage() {
  const [apiBase, setApiBase] = useState(readInitialApiBase);
  const [apiKey, setApiKey] = useState('');
  const [activeTab, setActiveTab] = useState('products');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [productForm, setProductForm] = useState(productToForm());
  const [standardFoodForm, setStandardFoodForm] = useState(standardFoodToForm());
  const [nutritionForm, setNutritionForm] = useState(nutritionToForm());
  const [inventoryForm, setInventoryForm] = useState(inventoryToForm());
  const [policyForm, setPolicyForm] = useState(policyToForm());

  const writable = Boolean(summary?.writable);
  const products = summary?.products || [];
  const standardFoods = summary?.standard_product_nutrition || summary?.standard_foods || [];
  const productNutrition = summary?.product_nutrition || [];
  const mockInventory = summary?.mock_inventory || [];
  const policies = summary?.policies || [];
  const auditLogs = summary?.audit_logs || [];
  const operationalSummary = summary?.operational_summary || {};

  const reloadSummary = useCallback(async () => {
    setLoading(true);
    setError('');
    setMessage('');
    rememberApiBase(apiBase);
    try {
      const nextSummary = await requestJson(apiBase, '/v1/retail/admin/summary', {}, apiKey);
      setSummary(nextSummary);
      setProductForm(productToForm(nextSummary.products?.[0]));
      setStandardFoodForm(standardFoodToForm((nextSummary.standard_product_nutrition || nextSummary.standard_foods)?.[0]));
      setNutritionForm(nutritionToForm(nextSummary.product_nutrition?.[0]));
      setInventoryForm(inventoryToForm(nextSummary.mock_inventory?.[0]));
      setPolicyForm(policyToForm(nextSummary.policies?.[0]));
    } catch (err) {
      setError(err.message || '관리 데이터를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [apiBase, apiKey]);

  useEffect(() => {
    void reloadSummary();
  }, [reloadSummary]);

  const statCards = [
    { label: '대표상품', value: operationalSummary.product_count ?? products.length },
    { label: '단품 SKU', value: operationalSummary.sku_count ?? products.length },
    { label: '영양 커버리지', value: formatPercent(operationalSummary.nutrition_coverage_rate) },
    { label: '활성 정책', value: operationalSummary.active_policy_count ?? policies.filter((policy) => policy.active).length },
  ];

  const qualityCards = [
    { label: '활성 SKU', value: operationalSummary.active_sku_count ?? '-' },
    { label: '대표 영양', value: operationalSummary.standard_product_nutrition_count ?? standardFoods.length },
    { label: '영양 매핑', value: operationalSummary.product_nutrition_count ?? productNutrition.length },
    { label: 'Mock 재고', value: operationalSummary.mock_inventory_count ?? mockInventory.length },
    { label: '건강태그 누락', value: operationalSummary.missing_health_tag_count ?? '-' },
    { label: '영양매핑 누락', value: operationalSummary.missing_nutrition_mapping_count ?? '-' },
  ];

  const saveRequest = useCallback(
    async (path, payload) => {
      if (!writable) {
        setError('현재 데이터 소스는 읽기 전용입니다. DB 연결 후 수정할 수 있습니다.');
        return;
      }
      setSaving(true);
      setError('');
      setMessage('');
      try {
        const nextSummary = await requestJson(
          apiBase,
          path,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          },
          apiKey,
        );
        setSummary(nextSummary);
        setMessage('저장했습니다.');
      } catch (err) {
        setError(err.message || '저장에 실패했습니다.');
      } finally {
        setSaving(false);
      }
    },
    [apiBase, apiKey, writable],
  );

  const saveProduct = useCallback(async () => {
    if (!productForm.standard_product_id.trim()) {
      setError('표준 상품 ID를 입력하세요.');
      return;
    }
    await saveRequest(`/v1/retail/admin/products/${encodeURIComponent(productForm.standard_product_id.trim())}`, {
      standard_product_name: productForm.standard_product_name,
      category_id: productForm.category_id || null,
      category_name: productForm.category_name,
      product_type_code: productForm.product_type_code,
      product_type_name: productForm.product_type_name || null,
      gs_product_id: productForm.gs_product_id || null,
      gs_product_name: productForm.gs_product_name || null,
      is_primary_product: productForm.is_primary_product,
      sku_id: productForm.sku_id || null,
      sku_name: productForm.sku_name || null,
      sku_sales_channel_scope: fromCsv(productForm.sku_sales_channel_scope),
      sku_availability_scope: fromCsv(productForm.sku_availability_scope),
      sku_pilot_enabled: productForm.sku_pilot_enabled,
      barcode: productForm.barcode || null,
      gtin: productForm.gtin || null,
      item_report_no: productForm.item_report_no || null,
      manufacturer_name: productForm.manufacturer_name || null,
      product_health_tags: fromCsv(productForm.product_health_tags),
      product_health_tag_scores: readJson(productForm.product_health_tag_scores, {}),
      raw_payload: readJson(productForm.raw_payload, {}),
      actor: 'frontend-admin',
    });
  }, [productForm, saveRequest]);

  const saveStandardFood = useCallback(async () => {
    if (!standardFoodForm.standard_product_id.trim()) {
      setError('표준 상품 ID를 입력하세요.');
      return;
    }
    await saveRequest(`/v1/retail/admin/standard-product-nutrition/${encodeURIComponent(standardFoodForm.standard_product_id.trim())}`, {
      edible_state: standardFoodForm.edible_state,
      serving_basis: standardFoodForm.serving_basis,
      nutrition_facts: readJson(standardFoodForm.nutrition_facts, EMPTY_NUTRITION),
      nutrition_source: standardFoodForm.nutrition_source,
      source_ref: readJson(standardFoodForm.source_ref, {}),
      source_confidence: Number(standardFoodForm.source_confidence),
      actor: 'frontend-admin',
    });
  }, [saveRequest, standardFoodForm]);

  const saveNutrition = useCallback(async () => {
    if (!nutritionForm.gs_product_id.trim()) {
      setError('상품 ID를 입력하세요.');
      return;
    }
    await saveRequest(`/v1/retail/admin/product-nutrition/${encodeURIComponent(nutritionForm.gs_product_id.trim())}`, {
      standard_product_id: nutritionForm.standard_product_id || null,
      mapping_level: nutritionForm.mapping_level,
      serving_basis: nutritionForm.serving_basis,
      nutrition_facts: readJson(nutritionForm.nutrition_facts, EMPTY_NUTRITION),
      nutrition_source: nutritionForm.nutrition_source,
      nutrition_match_status: nutritionForm.nutrition_match_status,
      match_confidence: Number(nutritionForm.match_confidence),
      source_ref: readJson(nutritionForm.source_ref, {}),
      verified: nutritionForm.verified,
      actor: 'frontend-admin',
    });
  }, [nutritionForm, saveRequest]);

  const savePolicy = useCallback(async () => {
    if (!policyForm.policy_version.trim()) {
      setError('정책 버전을 입력하세요.');
      return;
    }
    await saveRequest(`/v1/retail/admin/policies/${encodeURIComponent(policyForm.policy_version.trim())}`, {
      policy_payload: readJson(policyForm.policy_payload, {}),
      active: policyForm.active,
      actor: 'frontend-admin',
    });
  }, [policyForm, saveRequest]);

  const saveInventory = useCallback(async () => {
    if (!inventoryForm.store_type.trim() || !inventoryForm.store_code.trim() || !inventoryForm.sku_id.trim()) {
      setError('점포유형, 점포코드, SKU ID를 입력하세요.');
      return;
    }
    await saveRequest(
      `/v1/retail/admin/mock-inventory/${encodeURIComponent(inventoryForm.store_type.trim())}/${encodeURIComponent(inventoryForm.store_code.trim())}/${encodeURIComponent(inventoryForm.sku_id.trim())}`,
      {
        gs_product_id: inventoryForm.gs_product_id || null,
        available: inventoryForm.available,
        stock_quantity: inventoryForm.stock_quantity === '' ? null : Number(inventoryForm.stock_quantity),
        inventory_status: inventoryForm.inventory_status,
        source: inventoryForm.source,
        raw_payload: readJson(inventoryForm.raw_payload, {}),
        actor: 'frontend-admin',
      },
    );
  }, [inventoryForm, saveRequest]);

  return (
    <div className="retail-admin-page">
      <header className="retail-admin-header">
        <div>
          <p className="retail-admin-eyebrow">Retail Intelligence</p>
          <h1>리테일 인텔리전스 관리</h1>
          <p>파일럿 상품, 대표 품목, 영양성분 매핑, 추천정책을 조회하고 등록/수정합니다.</p>
        </div>
        <button type="button" onClick={reloadSummary} disabled={loading}>
          {loading ? '조회 중' : '새로고침'}
        </button>
      </header>

      <section className="retail-admin-panel retail-admin-settings">
        <label>
          API 주소
          <input value={apiBase} onChange={(event) => setApiBase(event.target.value)} />
        </label>
        <label>
          API Key
          <input
            type="password"
            value={apiKey}
            onChange={(event) => setApiKey(event.target.value)}
            placeholder="인증 사용 시 입력"
          />
        </label>
        <div className={`retail-admin-source ${writable ? 'is-writable' : 'is-readonly'}`}>
          {summary ? `${summary.source} / ${writable ? '수정 가능' : '읽기 전용'}` : '연결 전'}
        </div>
      </section>

      {message ? <p className="retail-admin-message">{message}</p> : null}
      {error ? <p className="retail-admin-error">{error}</p> : null}

      <div className="retail-admin-stats">
        {statCards.map((card) => (
          <div key={card.label}>
            <span>{card.label}</span>
            <strong>{card.value}</strong>
          </div>
        ))}
      </div>

      <section className="retail-admin-quality-panel" aria-label="운영 점검">
        <div>
          <span>운영 점검</span>
          <strong>{summary ? '요약 생성 완료' : '연결 대기'}</strong>
        </div>
        <dl>
          {qualityCards.map((card) => (
            <div key={card.label}>
              <dt>{card.label}</dt>
              <dd>{card.value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <nav className="retail-admin-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={activeTab === tab.key ? 'is-active' : ''}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {activeTab === 'products' ? (
        <AdminSection
          title="대표 상품/단품"
          items={products}
          itemKey={(item) => `${item.standard_product_id}-${item.sku_id || 'master'}`}
          selectedKey={`${productForm.standard_product_id}-${productForm.sku_id || 'master'}`}
          itemLabel={(item) => `${item.standard_product_id} · ${item.standard_product_name}${item.sku_id ? ` · ${item.sku_id}` : ''}`}
          onSelect={(item) => setProductForm(productToForm(item))}
          onNew={() => setProductForm(productToForm())}
          disabled={saving || !writable}
          onSave={saveProduct}
        >
          <FormGrid>
            <TextInput label="표준 상품 ID" value={productForm.standard_product_id} onChange={(value) => setProductForm({ ...productForm, standard_product_id: value })} />
            <TextInput label="표준 상품명" value={productForm.standard_product_name} onChange={(value) => setProductForm({ ...productForm, standard_product_name: value })} />
            <TextInput label="카테고리 ID" value={productForm.category_id} onChange={(value) => setProductForm({ ...productForm, category_id: value })} />
            <TextInput label="카테고리명" value={productForm.category_name} onChange={(value) => setProductForm({ ...productForm, category_name: value })} />
            <SelectInput label="상품 유형 코드" value={productForm.product_type_code} options={['PRIMARY', 'PROCESSED', 'UNKNOWN']} onChange={(value) => setProductForm({ ...productForm, product_type_code: value })} />
            <TextInput label="상품 유형명" value={productForm.product_type_name} onChange={(value) => setProductForm({ ...productForm, product_type_name: value })} />
            <TextInput label="GS 대표상품 ID" value={productForm.gs_product_id} onChange={(value) => setProductForm({ ...productForm, gs_product_id: value })} />
            <TextInput label="GS 대표상품명" value={productForm.gs_product_name} onChange={(value) => setProductForm({ ...productForm, gs_product_name: value })} />
            <TextInput label="단품 SKU ID" value={productForm.sku_id} onChange={(value) => setProductForm({ ...productForm, sku_id: value })} />
            <TextInput label="단품명" value={productForm.sku_name} onChange={(value) => setProductForm({ ...productForm, sku_name: value })} />
            <TextInput label="단품 노출 채널" value={productForm.sku_sales_channel_scope} onChange={(value) => setProductForm({ ...productForm, sku_sales_channel_scope: value })} />
            <TextInput label="단품 파일럿 범위" value={productForm.sku_availability_scope} onChange={(value) => setProductForm({ ...productForm, sku_availability_scope: value })} />
            <TextInput label="바코드" value={productForm.barcode} onChange={(value) => setProductForm({ ...productForm, barcode: value })} />
            <TextInput label="GTIN" value={productForm.gtin} onChange={(value) => setProductForm({ ...productForm, gtin: value })} />
            <TextInput label="품목제조보고번호" value={productForm.item_report_no} onChange={(value) => setProductForm({ ...productForm, item_report_no: value })} />
            <TextInput label="제조사명" value={productForm.manufacturer_name} onChange={(value) => setProductForm({ ...productForm, manufacturer_name: value })} />
            <TextInput label="상품 건강태그" value={productForm.product_health_tags} onChange={(value) => setProductForm({ ...productForm, product_health_tags: value })} />
            <CheckInput label="1차 상품" checked={productForm.is_primary_product} onChange={(value) => setProductForm({ ...productForm, is_primary_product: value })} />
            <CheckInput label="단품 파일럿 사용" checked={productForm.sku_pilot_enabled} onChange={(value) => setProductForm({ ...productForm, sku_pilot_enabled: value })} />
            <p className="retail-admin-field-hint">허용 태그: {HEALTH_TAG_OPTIONS.join(', ')}</p>
            <JsonInput label="상품 건강태그 점수" value={productForm.product_health_tag_scores} onChange={(value) => setProductForm({ ...productForm, product_health_tag_scores: value })} />
            <JsonInput label="Raw Payload" value={productForm.raw_payload} onChange={(value) => setProductForm({ ...productForm, raw_payload: value })} />
          </FormGrid>
        </AdminSection>
      ) : null}

      {activeTab === 'standardProducts' ? (
        <AdminSection
          title="대표 상품 영양성분"
          items={standardFoods}
          itemKey="standard_product_id"
          selectedKey={standardFoodForm.standard_product_id}
          itemLabel={(item) => `${item.standard_product_id} · ${item.nutrition_source}`}
          onSelect={(item) => setStandardFoodForm(standardFoodToForm(item))}
          onNew={() => setStandardFoodForm(standardFoodToForm())}
          disabled={saving || !writable}
          onSave={saveStandardFood}
        >
          <FormGrid>
            <TextInput label="표준 상품 ID" value={standardFoodForm.standard_product_id} onChange={(value) => setStandardFoodForm({ ...standardFoodForm, standard_product_id: value })} />
            <TextInput label="섭취 상태" value={standardFoodForm.edible_state} onChange={(value) => setStandardFoodForm({ ...standardFoodForm, edible_state: value })} />
            <TextInput label="기준 단위" value={standardFoodForm.serving_basis} onChange={(value) => setStandardFoodForm({ ...standardFoodForm, serving_basis: value })} />
            <TextInput label="영양 출처" value={standardFoodForm.nutrition_source} onChange={(value) => setStandardFoodForm({ ...standardFoodForm, nutrition_source: value })} />
            <NumberInput label="신뢰도" value={standardFoodForm.source_confidence} onChange={(value) => setStandardFoodForm({ ...standardFoodForm, source_confidence: value })} />
            <JsonInput label="Source Ref" value={standardFoodForm.source_ref} onChange={(value) => setStandardFoodForm({ ...standardFoodForm, source_ref: value })} />
            <JsonInput label="영양성분" value={standardFoodForm.nutrition_facts} onChange={(value) => setStandardFoodForm({ ...standardFoodForm, nutrition_facts: value })} />
          </FormGrid>
        </AdminSection>
      ) : null}

      {activeTab === 'nutrition' ? (
        <AdminSection
          title="상품 영양 매핑"
          items={productNutrition}
          itemKey={(item) => `${item.gs_product_id}-${item.mapping_level}`}
          selectedKey={`${nutritionForm.gs_product_id}-${nutritionForm.mapping_level}`}
          itemLabel={(item) => `${item.gs_product_id} · ${item.mapping_level} · ${item.nutrition_match_status}`}
          onSelect={(item) => setNutritionForm(nutritionToForm(item))}
          onNew={() => setNutritionForm(nutritionToForm())}
          disabled={saving || !writable}
          onSave={saveNutrition}
        >
          <FormGrid>
            <TextInput label="GS 대표상품 ID" value={nutritionForm.gs_product_id} onChange={(value) => setNutritionForm({ ...nutritionForm, gs_product_id: value })} />
            <TextInput label="표준 상품 ID" value={nutritionForm.standard_product_id} onChange={(value) => setNutritionForm({ ...nutritionForm, standard_product_id: value })} />
            <SelectInput label="매핑 레벨" value={nutritionForm.mapping_level} options={['SKU', 'STANDARD_PRODUCT', 'STANDARD_ITEM', 'NONE']} onChange={(value) => setNutritionForm({ ...nutritionForm, mapping_level: value })} />
            <TextInput label="기준 단위" value={nutritionForm.serving_basis} onChange={(value) => setNutritionForm({ ...nutritionForm, serving_basis: value })} />
            <TextInput label="영양 출처" value={nutritionForm.nutrition_source} onChange={(value) => setNutritionForm({ ...nutritionForm, nutrition_source: value })} />
            <SelectInput label="매칭 상태" value={nutritionForm.nutrition_match_status} options={['GS_PROVIDED', 'STANDARD_PRODUCT_MATCH', 'STANDARD_ITEM_MATCH', 'EXACT_MATCH', 'CANDIDATE_MATCH', 'LOW_CONFIDENCE', 'MISSING_NUTRITION', 'MISSING_STANDARD_PRODUCT', 'MISSING_STANDARD_ITEM']} onChange={(value) => setNutritionForm({ ...nutritionForm, nutrition_match_status: value })} />
            <NumberInput label="신뢰도" value={nutritionForm.match_confidence} onChange={(value) => setNutritionForm({ ...nutritionForm, match_confidence: value })} />
            <CheckInput label="검증 완료" checked={nutritionForm.verified} onChange={(value) => setNutritionForm({ ...nutritionForm, verified: value })} />
            <JsonInput label="Source Ref" value={nutritionForm.source_ref} onChange={(value) => setNutritionForm({ ...nutritionForm, source_ref: value })} />
            <JsonInput label="영양성분" value={nutritionForm.nutrition_facts} onChange={(value) => setNutritionForm({ ...nutritionForm, nutrition_facts: value })} />
          </FormGrid>
        </AdminSection>
      ) : null}

      {activeTab === 'inventory' ? (
        <AdminSection
          title="Mock 점포 재고"
          items={mockInventory}
          itemKey={(item) => `${item.store_type}-${item.store_code}-${item.sku_id}`}
          selectedKey={`${inventoryForm.store_type}-${inventoryForm.store_code}-${inventoryForm.sku_id}`}
          itemLabel={(item) => `${item.store_type} · ${item.store_code} · ${item.sku_id} · ${item.available ? '재고 있음' : '품절'}`}
          onSelect={(item) => setInventoryForm(inventoryToForm(item))}
          onNew={() => setInventoryForm(inventoryToForm())}
          disabled={saving || !writable}
          onSave={saveInventory}
        >
          <FormGrid>
            <SelectInput label="점포유형" value={inventoryForm.store_type} options={['GS_THE_FRESH', 'GS25']} onChange={(value) => setInventoryForm({ ...inventoryForm, store_type: value })} />
            <TextInput label="점포코드" value={inventoryForm.store_code} onChange={(value) => setInventoryForm({ ...inventoryForm, store_code: value })} />
            <TextInput label="단품 SKU ID" value={inventoryForm.sku_id} onChange={(value) => setInventoryForm({ ...inventoryForm, sku_id: value })} />
            <TextInput label="GS 대표상품 ID" value={inventoryForm.gs_product_id} onChange={(value) => setInventoryForm({ ...inventoryForm, gs_product_id: value })} />
            <SelectInput label="재고 상태 코드" value={inventoryForm.inventory_status} options={['IN_STOCK', 'LOW_STOCK', 'OUT_OF_STOCK', 'DISCONTINUED', 'UNKNOWN']} onChange={(value) => setInventoryForm({ ...inventoryForm, inventory_status: value })} />
            <NumberInput label="Mock 수량" value={inventoryForm.stock_quantity} onChange={(value) => setInventoryForm({ ...inventoryForm, stock_quantity: value })} />
            <TextInput label="출처" value={inventoryForm.source} onChange={(value) => setInventoryForm({ ...inventoryForm, source: value })} />
            <CheckInput label="추천 가능 재고" checked={inventoryForm.available} onChange={(value) => setInventoryForm({ ...inventoryForm, available: value })} />
            <p className="retail-admin-field-hint">
              재고 확인 옵션이 켜진 추천 요청에서는 같은 점포유형/점포코드의 SKU만 검증합니다. available이 꺼져 있거나 수량이 0 이하이면 추천 후보에서 제외됩니다.
            </p>
            <JsonInput label="Raw Payload" value={inventoryForm.raw_payload} onChange={(value) => setInventoryForm({ ...inventoryForm, raw_payload: value })} />
          </FormGrid>
        </AdminSection>
      ) : null}

      {activeTab === 'policies' ? (
        <AdminSection
          title="추천 정책"
          items={policies}
          itemKey="policy_version"
          selectedKey={policyForm.policy_version}
          itemLabel={(item) => `${item.policy_version}${item.active ? ' · active' : ''}`}
          onSelect={(item) => setPolicyForm(policyToForm(item))}
          onNew={() => setPolicyForm(policyToForm())}
          disabled={saving || !writable}
          onSave={savePolicy}
        >
          <FormGrid>
            <TextInput label="정책 버전" value={policyForm.policy_version} onChange={(value) => setPolicyForm({ ...policyForm, policy_version: value })} />
            <CheckInput label="활성 정책" checked={policyForm.active} onChange={(value) => setPolicyForm({ ...policyForm, active: value })} />
            <JsonInput label="정책 Payload" value={policyForm.policy_payload} onChange={(value) => setPolicyForm({ ...policyForm, policy_payload: value })} />
            <p className="retail-admin-field-hint retail-admin-policy-hint">
              현재 추천 API는 파일 기반 정책을 사용합니다. 이 화면의 정책은 등록/검토/감사 이력용으로 저장되며, 런타임 반영은 DB 활성 정책 로더를 연결한 뒤 적용됩니다.
            </p>
          </FormGrid>
        </AdminSection>
      ) : null}

      <section className="retail-admin-panel">
        <div className="retail-admin-panel-title">
          <h2>변경 이력</h2>
          <span>{auditLogs.length}건</span>
        </div>
        <div className="retail-admin-audit">
          {auditLogs.length ? (
            auditLogs.map((item, index) => (
              <div key={`${item.target_type}-${item.target_id}-${index}`}>
                <strong>{item.target_type}</strong>
                <span>{item.target_id}</span>
                <span>{item.action}</span>
                <small>{item.created_at || '-'}</small>
              </div>
            ))
          ) : (
            <p>변경 이력이 없습니다.</p>
          )}
        </div>
      </section>
    </div>
  );
}

function AdminSection({ title, items, itemKey, selectedKey, itemLabel, onSelect, onNew, disabled, onSave, children }) {
  const keyOf = (item) => (typeof itemKey === 'function' ? itemKey(item) : item[itemKey]);
  return (
    <section className="retail-admin-panel retail-admin-editor">
      <div className="retail-admin-panel-title">
        <h2>{title}</h2>
        <div>
          <button type="button" onClick={onNew}>새 항목</button>
          <button type="button" onClick={onSave} disabled={disabled}>저장</button>
        </div>
      </div>
      <div className="retail-admin-editor-grid">
        <div className="retail-admin-list">
          {items.length ? (
            items.map((item) => {
              const itemKeyValue = keyOf(item);
              const isSelected = selectedKey && String(itemKeyValue) === String(selectedKey);
              return (
                <button
                  key={itemKeyValue}
                  type="button"
                  className={isSelected ? 'is-selected' : ''}
                  onClick={() => onSelect(item)}
                >
                  <span>{itemLabel(item)}</span>
                </button>
              );
            })
          ) : (
            <p>데이터가 없습니다.</p>
          )}
        </div>
        <div className="retail-admin-form">{children}</div>
      </div>
    </section>
  );
}

function FormGrid({ children }) {
  return <div className="retail-admin-form-grid">{children}</div>;
}

function TextInput({ label, value, onChange }) {
  return (
    <label>
      {label}
      <input value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function NumberInput({ label, value, onChange }) {
  return (
    <label>
      {label}
      <input type="number" step="0.01" value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function SelectInput({ label, value, options, onChange }) {
  return (
    <label>
      {label}
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
    </label>
  );
}

function CheckInput({ label, checked, onChange }) {
  return (
    <label className="retail-admin-check">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      {label}
    </label>
  );
}

function JsonInput({ label, value, onChange }) {
  return (
    <label className="retail-admin-json">
      {label}
      <textarea value={value} onChange={(event) => onChange(event.target.value)} spellCheck={false} />
    </label>
  );
}

export default RetailIntelligenceAdminPage;
